/**
 * Resolucao da imobiliaria a partir do endereco.
 *
 * A plataforma serve varias imobiliarias do mesmo bundle. Quem decide de qual
 * delas e a pagina aberta e, primeiro, o subdominio — `imobx.seulugar.imb.br` —
 * e, se o ambiente ainda nao tiver subdominio, o primeiro segmento do caminho —
 * `/imobx/explore`. Quando nenhum dos dois traz slug, o site continua sendo o da
 * imobiliaria original, exatamente como antes.
 *
 * Os dois modos convivem de proposito: e o que permite ligar o DNS de
 * subdominio sem invalidar de uma vez todos os links `/imobx/...` ja
 * compartilhados por WhatsApp e e-mail.
 *
 * A decisao e sincrona: o router precisa do basename antes da primeira
 * renderizacao, e nao da para esperar uma chamada de API. A validacao de que o
 * slug existe de fato acontece depois, no `TenantGuard`.
 */

import { getHostRole, slugFromHost, tenantMode } from './host'
import { isReservedSegment, RESERVED_SEGMENTS } from './reserved'

export { RESERVED_SEGMENTS, isReservedSegment }

const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/

function firstSegment(pathname: string): string {
  return pathname.split('/').filter(Boolean)[0] || ''
}

function slugFromPath(pathname?: string): string | null {
  const path =
    pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '')
  const segment = firstSegment(path).toLowerCase()

  if (!segment) return null
  if (isReservedSegment(segment)) return null
  if (!SLUG_PATTERN.test(segment)) return null

  return segment
}

/**
 * Slug da imobiliaria do endereco atual, ou `null` quando a pagina pertence a
 * imobiliaria original ou a propria plataforma.
 *
 * Nos hosts de plataforma (`lp`, `cliente`) o retorno e sempre `null`: o
 * `/vistamar` de `cliente.seulugar.imb.br/vistamar` e parametro de rota, nao
 * contexto de dados — quem o interpreta e a pagina da porta de entrada.
 */
export function getTenantSlug(
  pathname?: string,
  hostname?: string
): string | null {
  const fromHost = slugFromHost(hostname)
  if (fromHost) return fromHost

  const role = getHostRole(hostname)
  if (role === 'landing' || role === 'client-gateway' || role === 'custom')
    return null

  if (tenantMode() === 'subdomain') return null

  return slugFromPath(pathname)
}

/**
 * Basename para o `BrowserRouter`.
 *
 * So existe quando o slug veio do caminho: ai o React Router acrescenta e
 * remove o prefixo em todos os links, e o resto da aplicacao segue escrevendo
 * `/explore` sem saber que ele existe.
 *
 * Quando o slug vem do subdominio o basename precisa ser `undefined`. Devolver
 * `/imobx` ali faria o router procurar um prefixo que a URL nao tem, e toda
 * navegacao cairia na rota `*`.
 */
export function getTenantBasename(
  pathname?: string,
  hostname?: string
): string | undefined {
  if (slugFromHost(hostname)) return undefined
  if (getHostRole(hostname) !== 'root') return undefined
  if (tenantMode() === 'subdomain') return undefined

  const slug = slugFromPath(pathname)
  return slug ? `/${slug}` : undefined
}
